import React from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { Controller } from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  Card,
  CardHeader,
  CardContent,
  Checkbox,
} from '@roger/ui-components';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useSchedulingStepForm } from '../../hooks/useCreatePatientForm/useSchedulingStepForm';
import { KinnserSchedulingStepFormContent } from '../../pages/CreatePatientForm/KinnserSchedulingStepFormContent';
import { RogerSchedulingStepFormContent } from '../../pages/CreatePatientForm/RogerSchedulingStepFormContent';
import { SchedulingStepEmptyState } from '../../pages/CreatePatientForm/SchedulingStepEmptyState';
import { FeatureFlag } from '../../constants/featureFlags';
import MarkCompletedButton from './MarkCompletedButton';

interface SchedulingStepProps {
  onStatusChange?: () => void | Promise<void>;
}

const SchedulingStep: React.FC<SchedulingStepProps> = ({ onStatusChange }) => {
  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );

  const {
    // Form
    control,
    register,
    errors,
    setValue,
    handleFieldBlur,

    // Watched values
    kinnserCreateEpisode,
    rogerCreateEpisode,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics
    clinicData,
    loadingClinicData,

    // Intake data
    selectedIntake,
  } = useSchedulingStepForm();

  // Check if user has permission to create patients
  if (!hasFeatureFlag(FeatureFlag.INTAKE_SCHEDULING_SUPPORT)) {
    return (
      <div className='p-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to create patients. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  // Show loading spinner for entire section while clinic info is loading
  if (loadingClinicData) {
    return (
      <div className='h-full flex flex-col'>
        <div className='h-full flex items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-500'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='text-xs text-green-600'>Saved</div>
            )}
            {saveStatus === 'error' && (
              <div className='text-xs text-red-600'>Error saving</div>
            )}
          </div>
          {selectedIntake && (
            <MarkCompletedButton
              intakeId={selectedIntake.id}
              tabKey='scheduling'
              isComplete={
                selectedIntake.tab_statuses?.scheduling === 'complete' ||
                selectedIntake.tab_statuses?.scheduling ===
                  'complete_with_errors'
              }
              onStatusChange={onStatusChange}
              className='ml-2'
            />
          )}
        </div>
        <div className='flex items-center gap-6'>
          {hasFeatureFlag(FeatureFlag.INTAKE_ROGER_SCHEDULING) && (
            <div className='flex items-center gap-2'>
              <Controller
                name='roger_create_episode'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    id='createRogerEpisodeEnabled'
                    checked={!!value}
                    onCheckedChange={async checked => {
                      const isChecked = checked === true;
                      onChange(isChecked);

                      // If checking Roger, uncheck Kinnser
                      if (isChecked) {
                        setValue('kinnser_create_episode', false);
                        setValue('roger_create_episode', true);
                        setValue('push_to_roger', true);
                        setValue('push_to_kinnser', false);
                        // Save with Roger checked and Kinnser unchecked
                        await saveToServer({
                          roger_create_episode: true,
                          kinnser_create_episode: false,
                          push_to_roger: true,
                          push_to_kinnser: false,
                        });
                      } else {
                        // Just uncheck Roger, leave Kinnser as is
                        setValue('push_to_roger', false);
                        await saveToServer({
                          roger_create_episode: false,
                        });
                      }
                    }}
                  />
                )}
              />
              <label
                htmlFor='createRogerEpisodeEnabled'
                className='text-sm font-medium cursor-pointer'
              >
                Schedule SOC for Roger
              </label>
            </div>
          )}
          {hasFeatureFlag(FeatureFlag.INTAKE_WELLSKY_SCHEDULING) && (
            <div className='flex items-center gap-2'>
              <Controller
                name='kinnser_create_episode'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    id='createEpisodeEnabled'
                    checked={!!value}
                    onCheckedChange={async checked => {
                      const isChecked = checked === true;
                      onChange(isChecked);

                      // If checking Kinnser, uncheck Roger
                      if (isChecked) {
                        setValue('roger_create_episode', false);
                        setValue('kinnser_create_episode', true);
                        setValue('push_to_kinnser', true);
                        setValue('push_to_roger', false);
                        // Save with Kinnser checked and Roger unchecked
                        await saveToServer({
                          kinnser_create_episode: true,
                          roger_create_episode: false,
                          push_to_kinnser: true,
                          push_to_roger: false,
                        });
                      } else {
                        // Just uncheck Kinnser, leave Roger as is
                        setValue('push_to_kinnser', false);
                        await saveToServer({
                          kinnser_create_episode: false,
                        });
                      }
                    }}
                  />
                )}
              />
              <label
                htmlFor='createEpisodeEnabled'
                className='text-sm font-medium cursor-pointer'
              >
                Schedule SOC for Kinnser
              </label>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        {kinnserCreateEpisode ? (
          <KinnserSchedulingStepFormContent
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            handleFieldBlur={handleFieldBlur}
            saveToServer={saveToServer}
            clinicData={clinicData}
            loadingClinicData={loadingClinicData}
            createResult={null}
          />
        ) : rogerCreateEpisode ? (
          <RogerSchedulingStepFormContent
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            handleFieldBlur={handleFieldBlur}
            saveToServer={saveToServer}
            clinicData={clinicData}
            loadingClinicData={loadingClinicData}
          />
        ) : (
          <SchedulingStepEmptyState />
        )}
      </CardContent>
    </Card>
  );
};

export default SchedulingStep;
